import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    IconButton,
    InputAdornment,
    OutlinedInput,
    TableHead,
    Table,
    TableBody,
    TableCell,
    TablePagination,
    TableRow,
    TableFooter,
    Toolbar,
    Tooltip,
    Typography
} from "@mui/material";
import FeatherIcon from "feather-icons-react";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

import Breadcrumb from "../../../layouts-v2/full-layout/breadcrumb/Breadcrumb";

import PageContainer from "../../../components-v2/container/PageContainer";
import Loading from "../../../layouts-v2/loading/Loading";
import eitri_placeholder from "../../../assets-v2/images/placeholder/eitri-placeholder.svg";
import { Link } from "react-router-dom";

import { EitriAppService } from "../../../services-v2/eitri-manager-api/EitriAppService";
import { EitriAppStatus } from "../../../models-v2/EitriApp";
import useAlert from "../../../hooks/useAlert";
import useStatus from "../../../hooks/useStatus";
function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = event => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = event => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = event => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = event => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === "rtl" ? (
                    <LastPageIcon />
                ) : (
                    <FirstPageIcon />
                )}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === "rtl" ? (
                    <KeyboardArrowRight />
                ) : (
                    <KeyboardArrowLeft />
                )}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === "rtl" ? (
                    <KeyboardArrowLeft />
                ) : (
                    <KeyboardArrowRight />
                )}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === "rtl" ? (
                    <FirstPageIcon />
                ) : (
                    <LastPageIcon />
                )}
            </IconButton>
        </Box>
    );
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired
};

const ListEitriApp = () => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [eitriApps, setEitriApps] = React.useState([]);
    const [totalEitriApps, setTotalEitriApps] = React.useState(0);
    const { alertInfo, setAllAlertInfo, setInitialAlertInfo } = useAlert();
    const { status, setAllStatus } = useStatus();
    const [isFilterInputVisible, setIsFilterInputVisible] = React.useState(
        false
    );
    const [appName, setAppName] = React.useState("");
    const [delay] = React.useState(300);

    React.useEffect(() => {
        const newPage = page + 1;
        fetchEitriApps(rowsPerPage, newPage);
    }, []);

    React.useEffect(() => {
        const delayInputTimeoutId = setTimeout(async () => {
            const response = await EitriAppService.getEitriAppsByName(
                rowsPerPage,
                page + 1,
                appName
            );
            updateEitriAppState(response);
        }, delay);
        return () => clearTimeout(delayInputTimeoutId);
    }, [appName, delay]);

    async function fetchEitriApps(appsPerPage, page) {
        setAllStatus({ isFetching: true, success: false, error: false });
        try {
            const response = await EitriAppService.getEitriApps(
                appsPerPage,
                page
            );
            if (response.data.length === 0) {
                setAllStatus({
                    isFetching: false,
                    success: true,
                    error: false
                });
                setAllAlertInfo({
                    theme: "warning",
                    message: `Você não está associado a nenhum Eitri App.`,
                    visible: true
                });
                return;
            }
            updateEitriAppState(response);
            setAllStatus({ isFetching: false, success: true, error: false });
        } catch (err) {
            setAllStatus({ isFetching: false, success: false, error: true });
            setAllAlertInfo({
                theme: "error",
                message: `Ops! Não foi possível carregar os Eitri Apps.`,
                visible: true
            });
            console.error(err);
        }
    }

    const updateEitriAppState = content => {
        setEitriApps(content.data);
        setTotalEitriApps(content.totalItems);
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalEitriApps) : 0;

    const handleChangePage = (event, newPage) => {
        const pageToLoadFromService = newPage + 1;
        setPage(newPage);
        fetchEitriApps(rowsPerPage, pageToLoadFromService);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        fetchEitriApps(event.target.value, 1);
        setPage(0);
    };

    async function filterAppsByName(e) {
        const name = e.target.value;
        setAppName(name);
    }

    const BCrumb = [
        {
            title: "Eitri Apps"
        }
    ];

    return (
        <>
            {status.isFetching ? (
                <Loading isVisible={status.isFetching} />
            ) : (
                <PageContainer
                    title="Eitri Apps"
                    description="this is Eitri Apps page"
                >
                    {/* breadcrumb */}
                    <Breadcrumb items={BCrumb} />
                    {/* end breadcrumb */}
                    <Card>
                        <Collapse in={alertInfo.visible}>
                            <Alert
                                variant="filled"
                                severity={alertInfo.theme || "info"}
                                action={
                                    <IconButton
                                        aria-label="close"
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            setInitialAlertInfo();
                                        }}
                                    >
                                        <FeatherIcon icon="x" width="20" />
                                    </IconButton>
                                }
                            >
                                {alertInfo.message}
                            </Alert>
                        </Collapse>

                        <Toolbar
                            sx={{
                                pl: { sm: 2 },
                                pr: { xs: 1, sm: 1 },
                                display: "flex",
                                justifyContent: "flex-end"
                            }}
                        >
                            {isFilterInputVisible ? (
                                <OutlinedInput
                                    endAdornment={
                                        <InputAdornment
                                            position="end"
                                            onClick={() => {
                                                setIsFilterInputVisible(false);
                                                setAppName("");
                                            }}
                                        >
                                            <FeatherIcon icon="x" width="20" />
                                        </InputAdornment>
                                    }
                                    id="username2-text"
                                    placeholder="Digite o nome do eitri app que você procura..."
                                    fullWidth
                                    size="small"
                                    value={appName}
                                    onChange={e => filterAppsByName(e)}
                                />
                            ) : (
                                <Tooltip title="Filter list">
                                    <IconButton
                                        onClick={() =>
                                            setIsFilterInputVisible(true)
                                        }
                                    >
                                        <FeatherIcon icon="filter" width="18" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Toolbar>

                        <CardContent>
                            <Box
                                sx={{
                                    overflow: {
                                        xs: "auto",
                                        md: "unset"
                                    }
                                }}
                            >
                                <Table
                                    aria-label="custom pagination table"
                                    sx={{
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="h5">
                                                    Eitri App
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="h5">
                                                    Status
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="h5">
                                                    Versões
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {eitriApps.map(eitriApp => (
                                            <TableRow key={eitriApp.id}>
                                                <TableCell>
                                                    <Link
                                                        to={{
                                                            pathname: `/eitri-app-management/eitri-apps/${eitriApp.id}`
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            sx={{
                                                                cursor:
                                                                    "pointer"
                                                            }}
                                                        >
                                                            <Avatar
                                                                src={
                                                                    eitri_placeholder
                                                                }
                                                                alt={
                                                                    "eitri logo"
                                                                }
                                                                width="30"
                                                                sx={{
                                                                    borderRadius:
                                                                        "100%"
                                                                }}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    ml: 2
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    fontWeight="600"
                                                                >
                                                                    {
                                                                        eitriApp.name
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        sx={{
                                                            backgroundColor:
                                                                eitriApp.status ===
                                                                EitriAppStatus.ACTIVE
                                                                    ? theme =>
                                                                          theme
                                                                              .palette
                                                                              .success
                                                                              .light
                                                                    : eitriApp.status ===
                                                                      EitriAppStatus.INACTIVE
                                                                    ? theme =>
                                                                          theme
                                                                              .palette
                                                                              .error
                                                                              .light
                                                                    : theme =>
                                                                          theme
                                                                              .palette
                                                                              .secondary
                                                                              .light,
                                                            color:
                                                                eitriApp.status ===
                                                                "ACTIVE"
                                                                    ? theme =>
                                                                          theme
                                                                              .palette
                                                                              .success
                                                                              .main
                                                                    : eitriApp.status ===
                                                                      "INACTIVE"
                                                                    ? theme =>
                                                                          theme
                                                                              .palette
                                                                              .error
                                                                              .main
                                                                    : theme =>
                                                                          theme
                                                                              .palette
                                                                              .secondary
                                                                              .main,
                                                            borderRadius: "6px",
                                                            pl: "3px",
                                                            pr: "3px"
                                                        }}
                                                        size="small"
                                                        label={
                                                            eitriApp.status ===
                                                            EitriAppStatus.ACTIVE
                                                                ? "ativo"
                                                                : "inativo"
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Link
                                                        to={{
                                                            pathname: `/eitri-app-management/eitri-apps/${eitriApp.id}/revisions`
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            Versões
                                                        </Button>
                                                    </Link>
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {emptyRows > 0 && (
                                            <TableRow
                                                style={{
                                                    height: 53 * emptyRows
                                                }}
                                            >
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TablePagination
                                                rowsPerPageOptions={[5, 10, 25]}
                                                colSpan={3}
                                                count={totalEitriApps}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                labelRowsPerPage={
                                                    "Itens por página:"
                                                }
                                                SelectProps={{
                                                    inputprops: {
                                                        "aria-label":
                                                            "rows per page"
                                                    },
                                                    native: true
                                                }}
                                                onPageChange={handleChangePage}
                                                onRowsPerPageChange={
                                                    handleChangeRowsPerPage
                                                }
                                                ActionsComponent={
                                                    TablePaginationActions
                                                }
                                            />
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </Box>
                        </CardContent>
                    </Card>
                </PageContainer>
            )}
        </>
    );
};

export default ListEitriApp;
